import {
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Box,
  Flex,
  Image,
  useColorModeValue,
  useToken,
  HStack,
  Card,
  CardBody,
  Badge,
  Icon,
} from '@chakra-ui/react';
import { MapPin, GlobeHemisphereWest } from '@phosphor-icons/react';
import { keyframes } from '@emotion/react';
import { useState, useEffect, useRef } from 'react';
// Icons removed since we're now using numbers instead of icons in circles
import { useEnhancedTexture } from '../../context/EnhancedTextureContext';

const float = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0); }
`;

const features = [
  {
    iconColorToken: 'interactive.primary',
    title: 'Global Coverage',
    description: 'Explore megalithic sites worldwide with precise GPS coordinates',
  },
  {
    iconColorToken: 'border.default',
    title: 'Advanced Analysis',
    description: 'AI-powered feature detection with classification system',
  },
  {
    iconColorToken: 'accent.highlight',
    title: 'Comprehensive Data',
    description: 'Access detailed profiles including construction techniques',
  },
  {
    iconColorToken: 'border.default',
    title: 'Community Driven',
    description: 'Join researchers to contribute discoveries and collaborate',
  },
];

export function FeaturesSection() {
  const { isTextureEnabled } = useEnhancedTexture();
  const [borderDefault] = useToken('colors', ['border.default']);
  const [compassRotation, setCompassRotation] = useState(0);
  const compassRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!compassRef.current) return;

      const compassRect = compassRef.current.getBoundingClientRect();
      const compassCenterX = compassRect.left + compassRect.width / 2;
      const compassCenterY = compassRect.top + compassRect.height / 2;

      const mouseX = event.clientX;
      const mouseY = event.clientY;

      const dy = mouseY - compassCenterY;
      const dx = mouseX - compassCenterX;
      const angleRad = Math.atan2(dy, dx);
      let angleDeg = angleRad * (180 / Math.PI);

      angleDeg += 90; 

      setCompassRotation(angleDeg);
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <Box
      bg="bg.default"
      py={{ base: 16, md: 20 }}
      position="relative"
      overflow="hidden"
      borderTop="1px solid"
      borderBottom="1px solid"
      borderColor="border.light"
      // Apply single texture based on color mode to the Box itself
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: isTextureEnabled ? useColorModeValue(
          "url('/textures/canvas.png')", // Light mode: canvas
          "url('/textures/leather.png')"     // Dark mode: leather
        ) : 'none',
        backgroundRepeat: 'repeat',
        backgroundSize: 'auto', // Or 'cover' if you prefer
        opacity: isTextureEnabled ? 0.5 : 0, // Increased opacity
        mixBlendMode: useColorModeValue('overlay', 'overlay'), // Changed dark mode to 'overlay'
        pointerEvents: 'none',
        zIndex: 0, // Ensure it's behind the Container's content but above Box's bg
        transition: 'opacity 0.3s ease-in-out',
      }}
    >
      {/* Decorative compass element */}
      {isTextureEnabled && (
        <Box
          position="absolute"
          top="20px"
          left="20px"
          width="120px"
          height="120px"
          opacity={0.3}
          zIndex={1} // Ensure compass is above the ::before pseudo-element of the parent Box
          display={{ base: 'none', xl: 'block' }}
        >
          <Image
            ref={compassRef}
            src="/textures/compass-rose.png"
            alt="Decorative compass rose"
            width="100%"
            height="100%"
            objectFit="contain"
            transform={`rotate(${compassRotation}deg)`}
            transition="transform 0.1s linear"
            sx={{
              '@media(prefers-reduced-motion: reduce)': {
                transform: 'none',
                transition: 'none',
              }
            }}
          />
        </Box>
      )}
      {/* Decorative ancient site illustration - MOVED HERE */}
      {isTextureEnabled && (
        <Box
          position="absolute"
          bottom="20px"
          right="20px"
          width="150px"
          height="100px"
          bgImage="url('/textures/sacsayink.png')"
          bgSize="contain"
          bgPosition="center"
          bgRepeat="no-repeat"
          opacity={0.2}
          zIndex={1} // Ensure this is also above the ::before of the parent Box
          display={{ base: 'none', xl: 'block' }}
        />
      )}
      <Container
        variant="journal"
        maxW="container.xl"
        position="relative"
        zIndex={1} // Ensure Container content is above the Box's ::before and decorative elements
        bg={useColorModeValue("bg.surface", "bg.surface._dark")}
        boxShadow="lg"
        borderRadius="md"
        py={{ base: 8, md: 12 }}
      >
        <VStack spacing={{ base: 10, md: 12 }} position="relative" zIndex={2}>
          {/* Enhanced Header */}
          <VStack spacing={6} textAlign="center">
            <HStack spacing={4} align="center">
              <Icon as={MapPin} boxSize={8} color="interactive.primary" />
              <Heading 
                as="h2" 
                size="2xl" 
                fontFamily="accent"
                letterSpacing="wide"
                color="text.default"
              >
                Platform Features
              </Heading>
              <Icon as={GlobeHemisphereWest} boxSize={8} color="interactive.primary" />
            </HStack>
            
            <Box w="120px" h="2px" bg="interactive.primary" borderRadius="full" opacity={0.6} />
            
            <Text 
              fontSize={{ base: "lg", md: "xl" }} 
              color="text.muted" 
              maxW="4xl"
              lineHeight="tall"
            >
              Discover the powerful tools and features that make Megalith Maps your
              resource for exploring, documenting, and understanding megalithic sites worldwide.
            </Text>
          </VStack>

          {/* Enhanced Feature Cards */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={{ base: 6, md: 8 }}>
            {features.map((feature, index) => (
              <Card
                key={index}
                bg={useColorModeValue('bg.surface', 'bg.surface')}
                border="2px solid"
                borderColor={useColorModeValue('border.light', 'border.light')}
                borderRadius="lg"
                transition="all 0.4s ease"
                _hover={{
                  transform: 'translateY(-8px) rotate(1deg)',
                  boxShadow: 'xl',
                  borderColor: feature.iconColorToken,
                }}
                position="relative"
                overflow="hidden"
                _before={{
                  content: '""',
                  position: 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  opacity: isTextureEnabled ? useColorModeValue(0.05, 0.08) : 0,
                  pointerEvents: 'none',
                  bgImage: isTextureEnabled ? 'var(--texture-aged)' : 'none',
                  bgRepeat: 'repeat',
                  bgSize: '200px',
                  bgBlendMode: useColorModeValue('multiply', 'soft-light'),
                  zIndex: 0,
                  borderRadius: 'inherit',
                  transition: 'opacity 0.3s ease-in-out',
                }}
                sx={{
                  '@media(prefers-reduced-motion: no-preference)': {
                    animation: `${float} 4s ease-in-out infinite`,
                    animationDelay: `${index * 0.3}s`,
                  }
                }}
              >
                <CardBody p={6} position="relative" zIndex={1}>
                  <VStack spacing={3} textAlign="center">
                    <Heading 
                      size="md" 
                      fontFamily="accent" 
                      color="text.default"
                      letterSpacing="wide"
                    >
                      {feature.title}
                    </Heading>
                    
                    <Text 
                      fontSize="sm" 
                      color="text.muted"
                      lineHeight="tall"
                      px={2}
                    >
                      {feature.description}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
}
